import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';

const COLUMNS = ['נדרש לפתח', 'בפיתוח', 'באגים', 'טופל'] as const;
type Column = typeof COLUMNS[number];

const USERS: Record<string, { color: string; short: string }> = {
  'גל':     { color: '#3B82F6', short: 'ג' },
  'ידידיה': { color: '#10B981', short: 'י' },
  'חלבה':   { color: '#8B5CF6', short: 'ח' },
  'ברו':    { color: '#F59E0B', short: 'ב' },
  'בוב':    { color: '#EF4444', short: '🐛' },
};

const PRIORITY_LABEL: Record<string, string> = { high: '🔴 גבוהה', medium: '🟡 בינונית', low: '🟢 נמוכה' };

interface Task {
  _id: string;
  title: string;
  description: string;
  column: Column;
  assignee: string;
  priority: 'high' | 'medium' | 'low';
  feature: string;
}

const EMPTY: Partial<Task> = { title: '', description: '', column: 'נדרש לפתח', assignee: '', priority: 'medium', feature: '' };

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '9px 12px', background: '#0f172a',
  border: '1px solid #334155', borderRadius: 6, color: '#e2e8f0',
  fontFamily: 'inherit', fontSize: 14, boxSizing: 'border-box',
};

export default function AgentsTasksPage() {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [modal, setModal] = useState(false);
  const [editTask, setEditTask] = useState<Task | null>(null);
  const [form, setForm] = useState<Partial<Task>>(EMPTY);
  const [dragId, setDragId] = useState<string | null>(null);
  const [activeCol, setActiveCol] = useState<Column>('נדרש לפתח');

  const load = useCallback(() => api.get('/tasks').then(r => setTasks(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const openNew = (col: Column) => { setEditTask(null); setForm({ ...EMPTY, column: col }); setModal(true); };
  const openEdit = (t: Task) => { setEditTask(t); setForm({ ...t }); setModal(true); };

  const save = async () => {
    if (!form.title?.trim()) return;
    if (editTask) await api.patch(`/tasks/${editTask._id}`, form);
    else await api.post('/tasks', form);
    setModal(false); load();
  };

  const del = async () => {
    if (editTask) { await api.delete(`/tasks/${editTask._id}`); setModal(false); load(); }
  };

  const onDrop = async (col: Column, e: React.DragEvent) => {
    e.preventDefault();
    if (dragId) { await api.patch(`/tasks/${dragId}`, { column: col }); load(); }
  };

  const colTasks = (col: Column) => tasks.filter(t => t.column === col);

  const Card = ({ task }: { task: Task }) => {
    const user = USERS[task.assignee];
    return (
      <div
        draggable
        onDragStart={() => setDragId(task._id)}
        onDragEnd={() => setDragId(null)}
        onClick={() => openEdit(task)}
        style={{
          background: '#0f172a', borderRadius: 7, padding: 12, cursor: 'pointer',
          border: '1px solid #334155', borderRight: `4px solid ${user?.color || '#475569'}`,
          marginBottom: 8,
        }}
      >
        <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>{task.title}</div>
        {task.description && (
          <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 6, lineHeight: 1.4 }}>
            {task.description.slice(0, 80)}{task.description.length > 80 ? '…' : ''}
          </div>
        )}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ fontSize: 11 }}>{PRIORITY_LABEL[task.priority]}</span>
          {user && (
            <span style={{ fontSize: 11, background: '#1e293b', padding: '2px 7px', borderRadius: 4, display: 'flex', gap: 4, alignItems: 'center' }}>
              <span style={{ background: user.color, color: 'white', borderRadius: '50%', width: 16, height: 16, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 9 }}>{user.short}</span>
              {task.assignee}
            </span>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <style>{`
        .board-page { direction: rtl; padding: 12px; background: #0f172a; color: #e2e8f0; min-height: 100vh; }
        .board-header { display: flex; align-items: center; gap: 12px; margin-bottom: 16px; }
        .board-title { font-size: 22px; font-weight: 800; color: #60a5fa; margin: 0; }
        .btn-new { background: #2563eb; color: white; border: none; border-radius: 8px; padding: 8px 16px; cursor: pointer; font-weight: 700; font-size: 13px; }

        /* Mobile tabs — always visible on small screens */
        .mobile-tabs { display: none; gap: 6px; margin-bottom: 12px; overflow-x: auto; padding-bottom: 4px; }
        .tab-btn { background: #1e293b; color: white; border: none; border-radius: 20px; padding: 6px 13px; cursor: pointer; white-space: nowrap; font-size: 12px; font-weight: 600; }
        .tab-btn.active { background: #2563eb; }

        /* Desktop grid */
        .desktop-board { display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 14px; }
        @media (min-width: 1400px) { .desktop-board { grid-template-columns: repeat(4, 1fr); } }
        .mobile-board { display: none; }

        .col-wrap { background: #1e293b; border-radius: 10px; padding: 14px; min-height: 300px; border: 1px solid #334155; }
        .col-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; padding-bottom: 8px; border-bottom: 2px solid #475569; }
        .col-title { font-weight: 700; font-size: 14px; }
        .col-count { background: #334155; border-radius: 20px; padding: 2px 10px; font-size: 12px; color: #94a3b8; }
        .btn-add { background: #1e3a5f; color: white; border: none; border-radius: 8px; padding: 7px 12px; cursor: pointer; font-weight: 700; font-size: 12px; width: 100%; margin-top: 4px; }

        @media (max-width: 1024px) {
          .desktop-board { display: none !important; }
          .mobile-board { display: block !important; }
          .mobile-tabs { display: flex !important; }
          .board-title { font-size: 18px; }
        }
      `}</style>

      <div className="board-page">
        <div className="board-header">
          <h1 className="board-title">🐛 לוח משימות</h1>
          <button className="btn-new" onClick={() => openNew(activeCol)}>+ חדש</button>
        </div>

        {/* Mobile tabs */}
        <div className="mobile-tabs">
          {COLUMNS.map(col => (
            <button key={col} className={`tab-btn ${activeCol === col ? 'active' : ''}`} onClick={() => setActiveCol(col)}>
              {col} ({colTasks(col).length})
            </button>
          ))}
        </div>

        {/* Desktop: 4 columns */}
        <div className="desktop-board">
          {COLUMNS.map(col => (
            <div key={col} className="col-wrap" onDragOver={e => e.preventDefault()} onDrop={e => onDrop(col, e)}>
              <div className="col-header">
                <span className="col-title">{col}</span>
                <span className="col-count">{colTasks(col).length}</span>
              </div>
              {colTasks(col).map(t => <Card key={t._id} task={t} />)}
              <button className="btn-add" onClick={() => openNew(col)}>+ הוסף</button>
            </div>
          ))}
        </div>

        {/* Mobile: single column */}
        <div className="mobile-board">
          <div className="col-wrap" onDragOver={e => e.preventDefault()} onDrop={e => onDrop(activeCol, e)}>
            {colTasks(activeCol).map(t => <Card key={t._id} task={t} />)}
            {colTasks(activeCol).length === 0 && (
              <div style={{ textAlign: 'center', color: '#475569', padding: 30, fontSize: 13 }}>אין כרטיסים</div>
            )}
            <button className="btn-add" onClick={() => openNew(activeCol)}>+ הוסף ל{activeCol}</button>
          </div>
        </div>

        {/* Modal */}
        {modal && (
          <div onClick={() => setModal(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
            <div onClick={e => e.stopPropagation()} style={{ background: '#1e293b', borderRadius: 12, padding: 22, width: '100%', maxWidth: 460, border: '1px solid #334155' }}>
              <h2 style={{ margin: '0 0 16px', color: '#60a5fa', fontSize: 17 }}>{editTask ? 'עריכה' : 'כרטיס חדש'}</h2>
              {['title', 'description', 'feature'].map(key => (
                <div key={key} style={{ marginBottom: 10 }}>
                  <label style={{ display: 'block', marginBottom: 3, fontSize: 12, color: '#cbd5e1', fontWeight: 600 }}>
                    {key === 'title' ? 'כותרת *' : key === 'description' ? 'תיאור' : "פיצ'ר"}
                  </label>
                  {key === 'description'
                    ? <textarea value={(form as any)[key] || ''} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} style={inputStyle} rows={3} />
                    : <input value={(form as any)[key] || ''} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} style={inputStyle} />}
                </div>
              ))}
              {[
                { label: 'עמודה', key: 'column', opts: [...COLUMNS] },
                { label: 'אחראי', key: 'assignee', opts: ['', ...Object.keys(USERS)] },
                { label: 'עדיפות', key: 'priority', opts: ['high', 'medium', 'low'] },
              ].map(({ label, key, opts }) => (
                <div key={key} style={{ marginBottom: 10 }}>
                  <label style={{ display: 'block', marginBottom: 3, fontSize: 12, color: '#cbd5e1', fontWeight: 600 }}>{label}</label>
                  <select value={(form as any)[key] || ''} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} style={inputStyle}>
                    {opts.map(o => <option key={o} value={o}>{o || '— לא מוקצה —'}</option>)}
                  </select>
                </div>
              ))}
              <div style={{ display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
                <button onClick={save} style={{ background: '#2563eb', color: 'white', border: 'none', borderRadius: 8, padding: '8px 16px', cursor: 'pointer', fontWeight: 700 }}>שמור</button>
                <button onClick={() => setModal(false)} style={{ background: '#374151', color: 'white', border: 'none', borderRadius: 8, padding: '8px 16px', cursor: 'pointer', fontWeight: 700 }}>ביטול</button>
                {editTask && <button onClick={del} style={{ background: '#dc2626', color: 'white', border: 'none', borderRadius: 8, padding: '8px 16px', cursor: 'pointer', fontWeight: 700 }}>מחק</button>}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
